import type {
  ProfileType,
  SwitchSubPortfolioCommandData,
  SwitchSubPortfolioCommandDataVFrame,
} from '@festina/unitlink-general-types/unitlink'
import { type Optional, typeUtils } from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { useDataContext } from '@festina-life/ui/theme'
import { useState } from 'react'

import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'
import type { CommandDataFormProps } from '../../commandActions/CommandDataFormAndSimulation'
import { LifeForm } from '../../forms/LifeForm'
import { LifeInput } from '../../inputs/LifeInput'
import { PartitionCommandSelectionTable } from '../SwitchSubPortfolioGroupCommand/PartitionCommandSelectionTable'
import { mapUpdatePartitionCommandSelectionsToVFrame } from '../SwitchSubPortfolioGroupCommand/utils'

const validateKey = typeUtils.createValidator<SwitchSubPortfolioCommandData>()

export const SwitchSubPortfolioForm = ({
  commandData,
  ...props
}: CommandDataFormProps<SwitchSubPortfolioCommandData>) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { applyDate } = useDataContext()
  const translateCover = useTranslateCover()
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')({
    commitmentDate: applyDate,
    currentOverrideCommitmentVariant: applyDate
      ? 'currentByCommitmentDate'
      : undefined,
  })
  const [switchCommandType, setSwitchCommandType] =
    useState<SwitchSubPortfolioCommandData['switchCommandType']>()

  const [subPortfolioKeys, setSubPortfolioKeys] = useState<
    SwitchSubPortfolioCommandData['subPortfolioKeys']
  >([])
  const [profileType, setProfileType] = useState<ProfileType>()

  const { data: mFrame } = useHook(
    'fetchers.unitlink.useFetchSwitchSubPortfolioFrame',
  )({
    ownerId,
    tenantId,
    requestBody: {
      _type: 'SwitchSubPortfolioCommandData',
      switchCommandType,
      subPortfolioKeys,
      partitionCommandSelections: profileType && [
        {
          _type: 'PartitionCommandSelection',
          profileType,
        },
      ],
      ...commandData,
    },
  })

  const renderCoverName = (subPortfolioKey: Optional<string>) => {
    if (!subPortfolioKey) {
      return '-'
    }

    const [policyKey, coverKey] = subPortfolioKey.split('_')
    return `${renderPolicy(policyKey)} - ${translateCover(coverKey)}`
  }

  const initialValues: SwitchSubPortfolioCommandData = {
    _type: 'SwitchSubPortfolioCommandData',
  }

  const isSinglePartition =
    !mFrame?.addPartitionCommandSelections &&
    mFrame?.updatePartitionCommandSelections?.length === 1

  const frame = props.shouldSkipFrame
    ? undefined
    : ({
        _type: 'SwitchSubPortfolioGroupCommandDataVFrame',
        switchCommandType: mFrame?.switchCommandType,
        subPortfolioKeys: mFrame?.subPortfolioKeys,
        partitionCommandSelections: mapUpdatePartitionCommandSelectionsToVFrame(
          mFrame?.updatePartitionCommandSelections,
        ),
      } satisfies SwitchSubPortfolioCommandDataVFrame)

  return (
    <LifeForm initialValues={initialValues} frame={frame} {...props}>
      <>
        <LifeInput
          fallbackInputType="string"
          name={validateKey('switchCommandType')}
          label={translate('switchSubPortfolioForm.switchCommandType')}
          renderFn={(value) =>
            translateEnum('unitLink.enum.SwitchCommandType', value)
          }
          onBlur={setSwitchCommandType}
        />
        <LifeInput
          fallbackInputType="array"
          name={validateKey('subPortfolioKeys')}
          label={translate('switchSubPortfolioForm.subPortfolioKeys')}
          renderFn={(value) => renderCoverName(value)}
          onBlur={setSubPortfolioKeys}
        />
        {isSinglePartition && (
          <LifeInput
            fallbackInputType="string"
            name={validateKey('partitionCommandSelections.0.profileType')}
            label={translate('editPartitionCommandSelectionModal.profileType')}
            renderFn={(value) =>
              translateEnum('unitLink.enum.ProfileType', value)
            }
            onBlur={setProfileType}
          />
        )}
        <PartitionCommandSelectionTable
          mFrame={mFrame}
          isSinglePartition={isSinglePartition}
        />
      </>
    </LifeForm>
  )
}
